import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Header Component
export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="bg-white shadow-sm fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/flow" className="text-2xl font-bold text-gray-900">
              <span className="text-teal-600">Love</span> Your Work
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/process-details" className="text-gray-700 hover:text-teal-600 transition-colors">PROGRAMME</Link>
            <Link to="/support-british-theatre" className="text-gray-700 hover:text-teal-600 transition-colors">WHILE WE WAIT</Link>
            <Link to="/podcast" className="text-gray-700 hover:text-teal-600 transition-colors">PODCAST</Link>
            <Link to="/resources" className="text-gray-700 hover:text-teal-600 transition-colors">RESOURCES</Link>
            <Link to="/flow/about" className="text-gray-700 hover:text-teal-600 transition-colors">ABOUT</Link>
          </nav>

          {/* CTA Button */}
          <a 
            href="https://app.usemotion.com/meet/craigwilmann/meeting" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hidden md:block bg-teal-600 text-white px-6 py-2 rounded-md hover:bg-teal-700 transition-colors font-medium"
          >
            BOOK A CALL
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-700"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col space-y-4">
              <Link to="/process-details" className="text-gray-700">PROGRAMME</Link>
              <Link to="/support-british-theatre" className="text-gray-700">WHILE WE WAIT</Link>
              <Link to="/podcast" className="text-gray-700">PODCAST</Link>
              <Link to="/resources" className="text-gray-700">RESOURCES</Link>
              <Link to="/flow/about" className="text-gray-700">ABOUT</Link>
              <a 
                href="https://app.usemotion.com/meet/craigwilmann/meeting" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-teal-600 text-white px-6 py-2 rounded-md w-full text-center block"
              >
                BOOK A CALL
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section Component
export const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-gray-50 to-gray-100 pt-20 pb-12 lg:pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="lg:pr-8">
            <h1 className="text-4xl lg:text-6xl font-serif font-bold text-gray-900 leading-tight mb-6">
              High performance and
              <span className="text-teal-600"> performance highs</span>
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Helping people discover an effortless way to work better, do better and feel better
            </p>
            
            <a 
              href="https://app.usemotion.com/meet/craigwilmann/meeting" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-coral-500 text-white px-8 py-4 rounded-md text-lg font-medium hover:bg-coral-600 transition-colors mb-3 inline-block" 
              style={{backgroundColor: '#FF6B6B'}}
            >
              Book a Call →
            </a>
            
            {/* Clients Include Section */}
            <div className="mt-3">
              <p className="text-sm text-gray-500 mb-4 font-medium">Clients include</p>
              <div className="flex items-center justify-start space-x-8">
                {/* Business Leader Logo - Left */}
                <div className="flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_web-continue/artifacts/rgp25p7y_Business-Leader-Logo.webp" 
                    alt="Business Leader" 
                    className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity duration-200"
                  />
                </div>
                
                {/* Financial Times Logo - Center */}
                <div className="flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_web-continue/artifacts/v9s6r5ne_FT%20Logo.jpg" 
                    alt="Financial Times" 
                    className="h-12 w-auto opacity-70 hover:opacity-100 transition-opacity duration-200"
                  />
                </div>
                
                {/* PDG Media Logo - Right */}
                <div className="flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_web-continue/artifacts/0siycaee_pdg_media_logo.jpeg" 
                    alt="PDG Media" 
                    className="h-10 w-auto opacity-70 hover:opacity-100 transition-opacity duration-200"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              {/* Stats section removed */}
            </div>
          </div>

          {/* Right Image */}
          <div className="lg:pl-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_web-continue/artifacts/z394bpby_Website%20photo.jpg" 
              alt="Craig Wilmann facilitating a business workshop with leaders"
              className="w-full h-auto rounded-lg shadow-xl"
            />
            <p className="text-sm text-gray-500 text-center mt-3 italic">Love Your Work founder, Craig Wilmann</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Problem Section Component
export const ProblemSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-8">
          Work less hard. Do much better.
        </h2>
        
        <p className="text-lg text-gray-600 mb-12 leading-relaxed">
          Thomas Edison might have invented the lightbulb, but his '1% inspiration, 99% perspiration' quote has kept people in the dark for over a hundred years. Edison was wrong. Trying harder is not the secret to better results. The default way in which most people approach their work is causing disengagement, burnout and missed opportunities.
        </p>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="text-center">
            <div className="bg-red-100 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <span className="text-red-600 text-2xl">😴</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Disengagement</h3>
            <p className="text-gray-600">90% of UK employees feel disengaged in their work - Gallup, 2023</p>
          </div>
          
          <div className="text-center">
            <div className="bg-red-100 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <span className="text-red-600 text-2xl">😵</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Burnout</h3>
            <p className="text-gray-600">48% of employees feel burnt out at work at least once a week - McKinsey Health Institute, 2023</p>
          </div>
          
          <div className="text-center">
            <div className="bg-red-100 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <span className="text-red-600 text-2xl">📉</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Missed Opportunities</h3>
            <p className="text-gray-600">A CEO's strategic thinking time can account for 45% of a company's performance - McKinsey & Co, 2023</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Process Section Component
export const ProcessSection = () => {
  const steps = [
    {
      number: "1",
      title: "Your Work",
      description: "You’ll transform how you work, making it feel effortless and enjoyable. You’ll go from trying to flying and will feel energised not drained."
    },
    {
      number: "2", 
      title: "Your Team",
      description: "Workshops and actionable steps tailored to the needs & personalities of your team, helping them to work better, feel better and do better. "
    },
    {
      number: "3",
      title: "Your Brand",
      description: "A page on our website, a digital badge & LinkedIn promotion celebrating your business' commitment to its people."
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-4">
            How it Works
          </h2>
          <p className="text-lg text-gray-600">
            The 3 pillars of the Love Your Work programme
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="text-center">
              <div className="bg-teal-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                {step.number}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Results Section Component  
export const ResultsSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-6">
              Why it works
            </h2>
            
            <p className="text-lg text-gray-600 mb-6">
              Typical working patterns often mean:
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <span className="text-red-500 mr-3">•</span>
                <span className="text-gray-700">Teams need constant oversight</span>
              </div>
              <div className="flex items-start">
                <span className="text-red-500 mr-3">•</span>
                <span className="text-gray-700">They work hard but progress is slow</span>
              </div>
              <div className="flex items-start">
                <span className="text-red-500 mr-3">•</span>
                <span className="text-gray-700">People are prone to burning out or checking out</span>
              </div>
              <div className="flex items-start">
                <span className="text-red-500 mr-3">•</span>
                <span className="text-gray-700">You're stuck in operations instead of driving growth</span>
              </div>
            </div>

            <p className="text-lg text-gray-600 mb-8">
              But when you and your colleagues Love Your Work:
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <span className="text-teal-500 mr-3">✓</span>
                <span className="text-gray-700">You can reliably access the flow state</span>
              </div>
              <div className="flex items-start">
                <span className="text-teal-500 mr-3">✓</span>
                <span className="text-gray-700">Teams become self-managing and highly engaged</span>
              </div>
              <div className="flex items-start">
                <span className="text-teal-500 mr-3">✓</span>
                <span className="text-gray-700">Work becomes energising instead of draining</span>
              </div>
              <div className="flex items-start">
                <span className="text-teal-500 mr-3">✓</span>
                <span className="text-gray-700">You have more time to drive the business forward</span>
              </div>
            </div>

            <a 
              href="https://app.usemotion.com/meet/craigwilmann/meeting" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-coral-500 text-white px-8 py-3 rounded-md font-medium hover:bg-coral-600 transition-colors inline-block" 
              style={{backgroundColor: '#FF6B6B'}}
            >
              Ready to transform your business? →
            </a>
          </div>

          {/* Right Image */}
          <div>
            <img 
              src="https://images.unsplash.com/photo-1557804506-669a67965ba0" 
              alt="Flow state in workplace"
              className="w-full h-auto rounded-lg shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Philosophy Section Component
export const PhilosophySection = () => {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-8">
          This doesn't simply improve effectiveness, it transforms lives
        </h2>
        
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          The key to all of this is the link between deep work and the flow state.  
        </p>

         <p className="text-lg text-gray-600 mb-8 leading-relaxed">
         Every great moment in your life has involved flow. Falling in love, holding your child in your arms for the first time, jumping out of an aeroplane, completing a marathon, getting lost in a book or a play or a concert. 
        </p>

        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          Flow is what we live for, it's what we spend our money on, it's why people gamble and get drunk and take drugs. But one of the easiest and most reliable ways to get you there is your work. And once you and your team can do this reliably, you’ll never look back. 
        </p>

        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          You’ll find joy in the ordinary and achieve the extraordinary.
        </p>
      </div>
    </section>
  );
};

// About Craig Section
export const AboutSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Image */}
          <div>
            <img 
              src="https://images.pexels.com/photos/5387236/pexels-photo-5387236.jpeg" 
              alt="Time management and focus"
              className="w-full h-auto rounded-lg shadow-xl"
            />
          </div>

          {/* Right Content */}
          <div>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-6">
              Meet Craig Wilmann
            </h2>
            
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              I'm Craig Wilmann, founder of Love Your Work. I'm also a playwright and short story writer 
              (under the name Doe Wilmann), a father (under the name 'Daddy'), and a business consultant, 
              facilitator and mentor (under the name… well, actually, they just call me Craig).
            </p>

            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              In my mid-twenties, while building a twenty-person, £4 million+ turnover business, 
              I experienced the crippling effects of burnout. It was the worst and best thing that 
              ever happened to me.
            </p>

            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Today, my biggest passion in life, aside from writing and looking after my daughter, is helping people to love their work. 
              Not simply to be better at it, or become less affected by it, but to genuinely and wholeheartedly love it.
            </p>

            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              I've worked with over 300 founders and CEOs. I know that it's not just difficult for one person to motivate a whole business. It's impossible. 
              So instead of putting the burden on yourself, let's create the environment for you and your colleagues to experience the joy of flow.
            </p>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Fun Boy Three and Bananarama had it right… 'It ain't what you do, it's the way that you do it.'
            </p>

            <button className="bg-teal-600 text-white px-8 py-3 rounded-md font-medium hover:bg-teal-700 transition-colors">
              Learn More About My Story
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Coaching Programs Section
// Testimonials Section Component
export const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "A brilliant experience. Helps us think differently about how we get our work done individually, and as a team.",
      name: "Claudia Bloomfield",
      title: "Head of Talent, FT Strategies"
    },
    {
      quote: "Changed the way I approach both professional and creative tasks, and has above all allowed me to \'do more with less\'. I'd recommend it to anyone looking to level up their team.",
      name: "Ross Hindle", 
      title: "Group COO, PDG Media"
    },
    {
      quote: "Craig is a remarkably creative thinker. His supportive nature and inclusive leadership style made a significant impact on our team's morale and productivity.",
      name: "Josef Woodward",
      title: "Managing Director, Churchgate Consulting"
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-4">
            What clients say...
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-gray-50 p-8 rounded-lg">
              <div className="mb-6">
                <svg className="w-8 h-8 text-teal-600 mb-4" fill="currentColor" viewBox="0 0 32 32">
                  <path d="M10 8c-3.3 0-6 2.7-6 6v10h8V14h-6c0-1.1.9-2 2-2s2-.9 2-2zm16 0c-3.3 0-6 2.7-6 6v10h8V14h-6c0-1.1.9-2 2-2s2-.9 2-2z"/>
                </svg>
                <p className="text-gray-700 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>
              </div>
              <div className="border-t pt-4">
                <p className="font-semibold text-gray-900">{testimonial.name}</p>
                <p className="text-sm text-gray-600">{testimonial.title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CoachingProgramsSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-4">
            What's the best way to get started?
          </h2>
          <p className="text-lg text-gray-600">
            Book a call with Love Your Work founder, Craig Wilmann
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Book a Call</h3>
            <p className="text-gray-600 mb-6">Let's discuss how to improve engagement and enjoyment in your business</p>
            
            <ul className="text-left mb-8 space-y-2">
              <li className="flex items-center text-gray-700">
                <span className="text-teal-500 mr-2">✓</span>
                No obligation conversation
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-teal-500 mr-2">✓</span>
                Free advice on deep work, time management and flow state
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-teal-500 mr-2">✓</span>
                Tips on how to love your work
              </li>
            </ul>
            
            <a 
              href="https://app.usemotion.com/meet/craigwilmann/meeting"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-coral-500 text-white px-6 py-3 rounded-md font-medium hover:bg-coral-600 transition-colors w-full block text-center" 
              style={{backgroundColor: '#FF6B6B'}}
            >
              BOOK A CALL
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <div className="text-2xl font-bold mb-4">
              <span className="text-teal-400">Love</span> Your Work
            </div>
            <p className="text-gray-400 mb-4">
              Transforming businesses by unlocking the flow state.
            </p>
            <p className="text-gray-400">
              High performance and performance highs.
            </p>
          </div>

          {/* Navigation Links */}
          <div>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/process-details" className="hover:text-white transition-colors">Programme</Link></li>
              <li><Link to="/support-british-theatre" className="hover:text-white transition-colors">While We Wait</Link></li>
              <li><Link to="/support-british-theatre" className="hover:text-white transition-colors">Support British Theatre</Link></li>
              <li><Link to="/resources" className="hover:text-white transition-colors">Resources</Link></li>
              <li><Link to="/podcast" className="hover:text-white transition-colors">Meaningless Problems Podcast</Link></li>
              <li><Link to="/faqs" className="hover:text-white transition-colors">FAQs</Link></li>
            </ul>
          </div>

          {/* Legal and Additional Links */}
          <div>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/flow/about" className="hover:text-white transition-colors">About</Link></li>
              <li><Link to="/testimonials" className="hover:text-white transition-colors">Testimonials</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">Terms and Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2025 Love Your Work. All rights reserved. | Founded by Craig Wilmann</p>
        </div>
      </div>
    </footer>
  );
};